/**
 * v2 (Project Independence) error-code catalog + reader (DNKADM-0049).
 *
 * Every v2 backend error returns the uniform envelope `{ code, message, status }`
 * (handoff §4.2). In dink-admin's client that envelope lands in `ApiError.details`
 * (see `src/api/client.ts` → `apiErrorFromBody`), so the fine-grained machine
 * `code` is at `error.details.code`. This module exposes:
 *
 *  - `V2ErrorCode` — the union of known codes, so a per-feature `XError.ts` map can
 *    discriminate on it exhaustively;
 *  - `v2ErrorCode(err)` — the single reader that pulls `details.code` off an
 *    `ApiError` (and is `undefined` for anything else).
 *
 * Convention for feature specs (0050–0062): branch on `v2ErrorCode(err)` for the
 * precise rule, and fall back to `serverMessage(err)` (`src/api/serverMessage.ts`,
 * already `scrubBrand`-ed) for the human text. The coarse `statusToCode()`
 * (`src/api/errors.ts`) still handles generic cases; `details.code` is the fine
 * discriminator. Unknown codes degrade gracefully to the message — this catalog is
 * the KNOWN set, not a closed world: a `string` returned by `v2ErrorCode` that is
 * not in `V2ErrorCode` is expected and handled by the message fallback.
 */
import { ApiError } from '@/api/errors';

/**
 * The known v2 error codes (handoff §4.2). Booking/availability codes are HTTP
 * 409 unless noted; the 400 codes (`STAFF_REQUIRED`, `RESOURCE_REQUIRED`,
 * `INVALID_SLOT_TIME`) are request-shape validations. Config: `CURRENCY_LOCKED`
 * (409), `UNKNOWN_TENANT` (404).
 */
export const V2_ERROR_CODES = [
  // Booking / availability
  'OUTSIDE_BOOKING_WINDOW',
  // Location operating hours (DNKADM-0272) — DISTINCT from `OUTSIDE_BOOKING_WINDOW`
  // above (the service's own bookable window): after-hours booking of a non-member
  // target is refused (a membership perk); an active member target is unaffected.
  'OUTSIDE_LOCATION_HOURS',
  // The SERVICE itself is missing/deactivated — NARROWED by DNKAPI-0444, which
  // split the variation/location cases below out of this previously-overloaded code.
  'SERVICE_INACTIVE',
  // DNKAPI-0444 (DNKADM-0289) availability precision — all 409:
  'VARIATION_INACTIVE', // the variation is missing/soft-deleted/deactivated
  'VARIATION_NOT_FOR_SERVICE', // active variation, but its serviceId ≠ the request's
  'LOCATION_INACTIVE', // the location is missing/soft-deleted/deactivated
  'SLOT_TAKEN',
  'SLOT_CONTENDED',
  'RESOURCE_FULL',
  'STAFF_UNAVAILABLE',
  'STAFF_CAP_EXCEEDED',
  'INSUFFICIENT_CREDITS',
  'DAILY_CAP_EXCEEDED',
  'NO_ACTIVE_MEMBERSHIP',
  'SERVICE_HAS_NO_ASSIGNABLE_STAFF',
  'SERVICE_HAS_NO_ASSIGNABLE_RESOURCE',
  'OVERLAPPING_RESERVATION',
  // DNKAPI-0542 (DNKADM-0386 U1): an admin write (assignment / details / confirm)
  // that would double-book a coach — 409, refused unless the request carries
  // `acknowledgeConflict: true`. The envelope additionally carries `conflicts[]`
  // (the booking(s) in the way), read by `parseStaffConflict` (`./reservations`).
  'STAFF_CONFLICT',
  // DNKAPI-0546 (DNKADM-0393 U5): a reschedule / Edit time that would hand the
  // booking to a DIFFERENT coach — 409, refused unless the request carries
  // `acknowledgeStaffChange: true`. The envelope additionally carries a
  // `staffChange` OBJECT (the outgoing and incoming coach + the server's reason),
  // read by `parseStaffChange` (`./reservations`). Mutually exclusive with
  // `STAFF_CONFLICT` by construction: 0542 fires only with a non-null admin staff
  // override, 0546 only with a null one — neither is the other's fallback.
  'STAFF_WILL_CHANGE',
  // DNKAPI-0549 (DNKADM-0396): the other three freeness gates an admin override used to
  // remove IN SILENCE — all 409, each refused unless its own acknowledge flag is set.
  // Unlike the two above, several of these can apply to ONE request, so the envelope
  // carries every applicable reason in `overrideBlocks` (read by `parseOverrideRefusal`,
  // `./reservations`) and the envelope's own `code`/`message` are the FIRST block's, by
  // the server's fixed precedence RESOURCE_CONFLICT → STAFF_OUTSIDE_HOURS → STAFF_OVER_CAP.
  //
  // `RESOURCE_CONFLICT` additionally carries the booking(s) holding the bay under a
  // TOP-LEVEL `resourceConflicts` key — deliberately NOT `conflicts`, which stays 1:1 with
  // `STAFF_CONFLICT` above so key presence remains a safe discriminator.
  'RESOURCE_CONFLICT', // the bay has no capacity left  → `acknowledgeResourceConflict`
  'STAFF_OUTSIDE_HOURS', // the coach is off shift      → `acknowledgeStaffSchedule`
  'STAFF_OVER_CAP', // the coach is past a cap          → `acknowledgeStaffSchedule`
  // DNKAPI-0636 (plain `{ code, message, status }` bodies, NO acknowledge flag) — both 409:
  'RESOURCE_OUTSIDE_HOURS', // the bay is closed at that time; no override books it
  // DNKAPI-0660 (DNKADM-0455): a decline sent `expectedStatus` and the locked booking is in
  // another status. 409, nothing written; the envelope carries `currentStatus` (wire value).
  'RESERVATION_STATUS_CHANGED',
  'RESERVATION_CHANGED', // another write changed the booking first; nothing written, reload
  'STAFF_REQUIRED', // 400
  'RESOURCE_REQUIRED', // 400
  'INVALID_SLOT_TIME', // 400
  // Config
  'CURRENCY_LOCKED', // 409
  'UNKNOWN_TENANT', // 404
  // Plan limits on Add (DNKAPI-0139 / DNKADM-0065). `PLAN_LIMIT_REACHED` (409) is
  // a real cap hit → the upgrade popup; `PLAN_VERIFY_FAILED` (503) is a transient
  // plan-service outage → a retry toast, NOT the popup. Distinguished by the body
  // `error` code (lifted into `details.code` by `apiErrorFromBody`).
  'PLAN_LIMIT_REACHED', // 409
  'PLAN_VERIFY_FAILED', // 503
  // Ask Dink (DNKAPI-0627 / DNKADM-0434). `ASSISTANT_DISABLED` (404) is the kill
  // switch — the panel and its top-bar button hide for the rest of the session;
  // `ASSISTANT_UNAVAILABLE` (503) is a provider outage or timeout;
  // `ASSISTANT_RATE_LIMITED` (429) is the per-user / per-tenant question limit.
  'ASSISTANT_DISABLED', // 404
  'ASSISTANT_UNAVAILABLE', // 503
  'ASSISTANT_RATE_LIMITED', // 429
  // DNKAPI-0651 / 0658 (DNKADM-0452): a refund refused because another refund of the same
  // payment was in flight — 409. `serverMessage` maps it to the short guard line.
  'REFUND_IN_PROGRESS',
  // DNKAPI-0690 (DNKADM-0475): a new card order refused because an earlier card order of the
  // same member at the same location is still PENDING or NEEDS_REVIEW — 409, nothing written.
  // The envelope names it (`openOrderId`/`openOrderNumber`/`openOrderStatus`/`contactId`),
  // read by `parseOrderNeedsAttention` (`./membershipOrders`).
  'ORDER_NEEDS_ATTENTION',
] as const;

/** A known v2 backend error code (the fine-grained `error.details.code`). */
export type V2ErrorCode = (typeof V2_ERROR_CODES)[number];

/**
 * Read the fine-grained v2 backend code (`error.details.code`) off a thrown
 * error. Returns the raw string when present on an `ApiError` (which MAY be a
 * known `V2ErrorCode` or an unknown future code — callers fall back to
 * `serverMessage`), or `undefined` for a non-`ApiError` / a missing code.
 */
export function v2ErrorCode(err: unknown): string | undefined {
  if (err instanceof ApiError) {
    const code = err.details?.['code'];
    return typeof code === 'string' ? code : undefined;
  }
  return undefined;
}

/** Type guard: is `code` one of the KNOWN v2 error codes? */
export function isV2ErrorCode(code: string | undefined): code is V2ErrorCode {
  return code !== undefined && (V2_ERROR_CODES as readonly string[]).includes(code);
}

/**
 * The plan-limit payload (DNKAPI-0139) carried on a `PLAN_LIMIT_REACHED` (409)
 * rejection of an Add create. `axis` is the entity being capped (`locations` /
 * `staff` / `resources`, but kept open as `string` so an unknown future axis
 * degrades to a generic noun in the popup). `limit`/`current` are the plan cap
 * and the active count (equal when blocked); `plan` is the plan name and **may be
 * `null`**. Every field is nullable so a missing/odd-typed field never throws.
 */
export interface PlanLimitInfo {
  axis: 'locations' | 'staff' | 'resources' | string;
  limit: number | null;
  current: number | null;
  plan: string | null;
}

/**
 * Parse the plan-limit payload off a thrown error — the single shared parse the
 * three Add flows reuse. Returns the `PlanLimitInfo` ONLY when the error is a
 * `PLAN_LIMIT_REACHED` cap hit (`v2ErrorCode(err) === 'PLAN_LIMIT_REACHED'`);
 * returns `null` for anything else (including `PLAN_VERIFY_FAILED`, which is a
 * transient retry, not a limit hit). Reads the fields `apiErrorFromBody`
 * preserved on `details.planLimit`, coercing each defensively: a missing or
 * wrong-typed field becomes a safe default (`axis: ''` → a generic noun;
 * numbers/`plan` → `null`). Never throws.
 */
export function parsePlanLimit(err: unknown): PlanLimitInfo | null {
  if (v2ErrorCode(err) !== 'PLAN_LIMIT_REACHED' || !(err instanceof ApiError)) return null;
  const raw = err.details?.['planLimit'];
  const src = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  return {
    axis: typeof src['axis'] === 'string' ? src['axis'] : '',
    limit: typeof src['limit'] === 'number' ? src['limit'] : null,
    current: typeof src['current'] === 'number' ? src['current'] : null,
    plan: typeof src['plan'] === 'string' ? src['plan'] : null,
  };
}
